<template>
  <CCard class="dataTable ImportHistory">
    <CCol sm="12" class="mb-5 p-0">
      <router-link
        :to="{ name: 'audience' }"
        class="text-decoration-none d-flex-inline align-items-center font-weight-normal text-gray-600 linkHover"
      >
        <CIcon name="arrow" :height="20" class="icon__rotateLeft mr-2" viewBox="0 0 32 32" />
        {{ $t('audience.history.button.back') }}
      </router-link>
    </CCol>

    <EmptyList
      :loading="loadingGetList"
      v-if="loadingGetList"
      :linkText="$t('audience.audienceList.button.import-contacts')"
      :linkTo="{ name: 'import-contacts' }"
      :title="$t('audience.audienceList.text.noAudience')"
      :subTitle="$t('audience.audienceList.text.importOrCreate')"
    />

    <CCol v-else>
      <CCardHeader class="dataTable__header d-flex justify-content-between px-0 pb-5">
        <div class="dataTable__search">
          <CInput
            type="search"
            :placeholder="$t('audience.history.search.placeholder')"
            addInputClasses="pl-5"
            class="iconSearchPlaceholder pb-0"
            v-model="filterList"
          >
          </CInput>
        </div>
        <div class="dataTable__buttonLink">
          <router-link class="btn btn-primary px-4 py-3 font-weight-bold text-uppercase" tag="button" :to="{ name: 'choose-methods' }">{{
            $t('audience.history.button.import-contact')
          }}</router-link>
        </div>
      </CCardHeader>

      <transition name="fade">
        <MenssageAlert :messages="messages" />
      </transition>

      <CCardBody class="importHistory__list pb-5" v-if="filteredList.length">
        <div v-for="(item, index) in filteredList" :key="index" class="text-gray-600 importHistory__listItem">
          <div class="d-flex justify-content-between align-items-center p-4">
            <div class="listItem__name d-flex align-items-center py-3">
              <CustomTooltip v-if="item.status === 'ABORTED_WITH_EXCESS_DUPLICATES'" placement="right">
                <CIcon :name="iconStatus(item)" size="custom-size" :height="25" viewBox="0 0 32 32" class="mr-3" :class="iconColorStatus(item)" />
                <template v-slot:content>
                  <div class="font-sm">
                    <h1 class="font-sm font-bold mb-2">{{ $t('audience.history.text.error.title') }}</h1>
                    <p class="mb-0">{{ $t('audience.history.text.error.ABORTED_WITH_EXCESS_DUPLICATES') }}</p>
                  </div>
                </template>
              </CustomTooltip>
              <CIcon
                v-else
                :name="iconStatus(item)"
                size="custom-size"
                :height="25"
                viewBox="0 0 32 32"
                class="mr-3"
                :class="iconColorStatus(item)"
              />
              <p class="h4 m-0 font-weight-normal">
                {{ importType(item.importType) }}
              </p>
            </div>
            <template v-if="item.status == 'NOT_PROCESSED'">
              <div class="d-flex w-100 align-items-center listItem__status">
                <CSpinner color="primary CSpinner custom mr-3" />
                {{ $t('audience.history.text.importing-new-contacts') }}
              </div>
            </template>
            <template v-else>
              <div class="listItem listItem__numbers text-uppercase">
                <div class="h5 font-weight-normal">
                  {{ new Intl.NumberFormat().format(item.totalRecipientsImported) }}
                </div>
                {{ $t('audience.history.button.submited') }}
              </div>
              <div class="listItem listItem__numbers text-uppercase">
                <div class="h5 font-weight-normal text-success">
                  {{ new Intl.NumberFormat().format(item.totalRecipientsValids) }}
                </div>
                {{ $t('audience.history.button.valid') }}
              </div>
              <div class="listItem listItem__numbers text-uppercase">
                <div class="h5 font-weight-normal text-danger">
                  {{ new Intl.NumberFormat().format(item.totalRecipientsInvalids) }}
                </div>

                <div class="d-flex align-items-center">
                  {{ $t('audience.history.button.invalids') }}
                  <span
                    v-if="item.totalRecipientsInvalids && isCurrentOrganizationBase"
                    @click.stop="item.modalInvalids = !item.modalInvalids"
                    class="cursor-pointer"
                  >
                    <CIcon v-if="item.isExport" name="arrowExport" class="" size="custom-size" :height="15" viewBox="0 0 32 32" />
                  </span>
                </div>

                <transition name="fade" v-if="item.modalInvalids">
                  <div class="dataTable__popUp popUp" @click.stop>
                    <div class="popUp__body shadow-sm">
                      <ChooseExport
                        @confirm="exportImportation({ type: 'invalids', importId: item.importId, ...$event })"
                        :title="'Export invalids'"
                        @cancel="closePopUp(item, 'modalInvalids')"
                      />
                    </div>
                  </div>
                </transition>
              </div>
              <div class="listItem listItem__numbers text-uppercase">
                <div class="h5 font-weight-normal text-warning">
                  {{ new Intl.NumberFormat().format(item.totalRecipientsDuplicates) }}
                </div>

                <div class="d-flex align-items-center">
                  {{ $t('audience.history.button.duplicated') }}
                  <span
                    v-if="Boolean(item.totalRecipientsDuplicates) && isCurrentOrganizationBase"
                    @click.stop="item.modalDuplicates = !item.modalDuplicates"
                    class="cursor-pointer"
                  >
                    <CIcon v-if="item.isExport" name="arrowExport" class="listItem__download" size="custom-size" :height="15" viewBox="0 0 32 32" />
                  </span>
                </div>

                <transition name="fade" v-if="item.modalDuplicates">
                  <div class="dataTable__popUp popUp" @click.stop>
                    <div class="popUp__body shadow-sm">
                      <ChooseExport
                        :title="'Export duplicates'"
                        @confirm="
                          exportImportation({
                            type: 'duplicates',
                            importId: item.importId,
                            ...$event,
                          })
                        "
                        @cancel="closePopUp(item, 'modalDuplicates')"
                      />
                    </div>
                  </div>
                </transition>
              </div>
              <div class="listItem d-flex align-items-center">
                <CIcon name="userBgFull" class="mr-2" :height="15" viewBox="0 0 32 32" />
                {{ $t('general.component.table.by') }} {{ item.user }}
              </div>
              <div class="listItem">
                <p class="m-0">
                  {{ isCompleted(Boolean(item.totalRecipientsImported)) }} on
                  {{ formatDate(item.createdDatetime) }}
                </p>
              </div>
              <div class="listItem">
                <a class="d-flex align-items-center justify-content-end text-gray-600 text-uppercase linkHover" @click.prevent="toggleCollapse(item)">
                  {{ $t('audience.history.text.detail') }}
                  <CIcon name="arrow" :height="12" class="icon__rotateRigth ml-1" viewBox="0 0 32 32" />
                </a>
              </div>
            </template>
          </div>
          <CCollapse :show="item.collapse" :duration="400" class="bg-gray-200 text-gray-600">
            <ul class="d-flex listItem__detail px-5 pt-4 mb-0" :show="item.collapse">
              <li class="listItem__detailItem">
                <div class="d-flex align-items-center">
                  <CIcon name="infoSquareFull" :height="18" class="mr-2" viewBox="0 0 32 32" />
                  {{ $t('audience.history.text.id') }}
                </div>
                {{ item.importId }}
              </li>
              <li class="listItem__detailItem">
                <div class="d-flex align-items-center text-uppercase">
                  <CIcon name="fileFull" :height="18" class="mr-2" viewBox="0 0 32 32" />
                  {{ $t('audience.history.text.fileName') }}
                </div>
                {{ item.fileNameUser || item.importType }}
              </li>
              <li class="listItem__detailItem text-uppercase">
                <div class="d-flex align-items-center">
                  <CIcon name="key" :height="18" class="mr-2" viewBox="0 0 32 32" />
                  {{ $t('audience.history.text.key') }}
                </div>
                {{ item.keyColumns.join(',') }}
              </li>
              <li class="listItem__detailItem text-uppercase">
                <div class="d-flex align-items-center">
                  <CIcon name="column" :height="18" class="mr-2" viewBox="0 0 32 32" />
                  {{ $t('audience.history.text.column') }}
                </div>
                {{ item.columns.join(', ') }}
              </li>
            </ul>
          </CCollapse>
        </div>
      </CCardBody>
    </CCol>
  </CCard>
</template>

<script>
import MenssageAlert from '@/components/MenssageAlert'
import EmptyList from '@/components/EmptyList'
import fileDownload from 'js-file-download'
import ChooseExport from '@/components/ChooseExport'
import CustomTooltip from '@/components/CustomTooltip.vue'
import { dateTimeFormatString } from '../../../utils/dateUtil'
import services from '@/services.js'
import { mapGetters } from 'vuex'

export default {
  name: 'ImportHistory',
  components: {
    MenssageAlert,
    ChooseExport,
    EmptyList,
    CustomTooltip,
  },
  data() {
    return {
      loading: false,
      filterList: '',
      listHistory: [],
      messages: [],
      loadingGetList: false,
      intervalTime: '',
      collapsed: [],
      openedDuplicate: [],
      openedInvalids: [],
    }
  },

  mounted() {
    this.$root.$on('update-close-popup', () => {
      this.closePopUp()
    })
    this.reloadTable()
  },

  beforeDestroy() {
    clearInterval(this.intervalTime)
    this.$root.$off('update-close-popup')
  },

  created() {
    this.loadingGetList = true
    this.getData()
  },

  computed: {
    ...mapGetters({
      isOrganizationBase: 'auth/isOrganizationBase',
    }),
    isCurrentOrganizationBase() {
      return this.isOrganizationBase(this.$route.params.organizationId)
    },
    filteredList() {
      return this.listHistory.filter((item, i, arr) => {
        return item.user ? item.user.toLowerCase().includes(this.filterList.toLowerCase()) : arr
      })
    },
  },

  methods: {
    reloadTable() {
      this.intervalTime = setInterval(() => {
        this.getData(false)
      }, this.$configs.VUE_APP_TIME_TABLE || 10000)
    },

    formatDate(date) {
      return dateTimeFormatString(date)
    },

    hasProcessed(list) {
      if (list.find((item) => item.status === 'NOT_PROCESSED')) {
        this.$store.commit('setToastMessage', {
          title: this.$t('popup-notice.title.import-audience'),
          text: this.$t('popup-notice.text.import-audience-on-proccess'),
        })
      }
    },

    iconColorStatus(item) {
      if (item.status == 'PROCESSED') {
        return 'text-success'
      }

      if (item.status == 'NOT_PROCESSED' && item.totalRecipientsImported > 0) {
        return 'text-danger'
      }

      if (item.status == 'NOT_PROCESSED' && item.totalRecipientsImported == 0) {
        return 'text-warning'
      }

      if (item.status == 'ABORTED_WITH_EXCESS_DUPLICATES') {
        return 'text-danger'
      }
    },

    iconStatus(item) {
      if (item.status == 'PROCESSED') {
        return 'arrowCircle'
      }

      if (item.status == 'NOT_PROCESSED' && item.totalRecipientsImported > 0) {
        return 'checkCircle'
      }

      if (item.status == 'NOT_PROCESSED' && item.totalRecipientsImported == 0) {
        return 'infoCircle'
      }

      if (item.status == 'ABORTED_WITH_EXCESS_DUPLICATES') {
        return 'notificationAlert'
      }
    },

    isCompleted(submited) {
      return submited ? this.$t('audience.history.button.submited') : this.$t('audience.history.button.incompleted')
    },

    importType(importType) {
      return importType == 'FILE' ? this.$t('audience.history.importType.file') : this.$t('audience.history.importType.manual')
    },

    closePopUp(item, modal) {
      if (!item) {
        this.listHistory.forEach((element) => {
          element.modalDuplicates = false
          element.modalInvalids = false
        })
        return
      }
      item[modal] = false
    },

    addPropertyCollape(el) {
      return {
        ...el,
        collapse: this.collapsed.includes(el.importId),
        modalDuplicates: false,
        modalInvalids: false,
      }
    },

    getData(loading = true) {
      this.loading = loading
      services
        .importHistory(this.$store.state.auth.organizationId)
        .then(({ data }) => {
          this.listHistory = data.data.map(this.addPropertyCollape)
        })
        .catch(({ response }) => {
          this.messages.push({
            status: 'danger',
            text: response.data.message,
          })
        })
        .finally(() => {
          this.loading = false
          this.loadingGetList = false
        })
    },

    exportImportation(event) {
      this.messages = []
      services
        .exportHistory(this.$store.state.auth.organizationId, event.type, event.importId, {
          format: event.format,
          delimiter: event.delimiter,
        })
        .then((response) => {
          fileDownload(response.data, event.type + event.importId, response.headers['content-type'])

          this.$store.commit('setToastMessage', {
            title: 'EXPORT OF AUDIENCE',
            text: 'Your request is being processed',
          })
        })
        .catch(({ response }) => {
          this.messages.push({
            status: 'danger',
            text: response.data.message || response.statusText,
          })
        })
    },

    toggleCollapse(item) {
      if (!this.collapsed.includes(item.importId)) {
        item.collapse = true
        this.collapsed.push(item.importId)
      } else {
        item.collapse = false
        this.collapsed = this.collapsed.filter((el) => el != item.importId)
      }
    },
  },

  watch: {
    listHistory: {
      handler(data) {
        this.hasProcessed(data)
      },
    },
  },
}
</script>
<style lang="scss">
.importHistory {
  &__list {
    padding: 0;
  }

  &__listItem {
    flex: 1;
    border-bottom: 1px solid $gray-500;

    &:hover {
      color: $gray-700;
    }

    .listItem {
      flex: 1 0 auto;
    }
  }
}

.listItem__download {
  cursor: pointer;
}

.listItem__name {
  flex: 0 0 20%;
  min-width: 250px;
}

.listItem__numbers {
  font-size: $font-size-xs;
  position: relative;
}

.listItem__detail {
  flex-wrap: wrap;
}

.listItem__detailItem {
  flex: 0 0 35%;
  margin-bottom: 2%;
}

.linkHover:hover {
  cursor: pointer;
}
</style>
